import * as React from 'react';
import { useCallback, useState } from 'react';
import { Page } from '@hotjar/webapp/components/Page';
import { Alert, Breadcrumbs, Button, PageHeader, Select, Text, tokens } from '@hotjar/hotsauce';
import { StyledInput as Input } from '@hotjar/webapp/features/Settings/styles';
import { SITE_INDUSTRY_CHOICES } from '@hotjar/webapp/constants/siteIndustries';
import { useOwnedAccount } from '@hotjar/account';
import { MultiSkuComparisonLinks } from '@hotjar/webapp/features/Billing/MultiSkuComparisonLinks/MultiSkuComparisonLinks';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { Trans, useTranslation } from 'react-i18next';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { Controller, useForm } from 'react-hook-form';
import { useCurrentUser } from '@hotjar/user';
import { validateHTTPSUrl } from '@hotjar/insights-common/lib/url';
import { css } from '@emotion/react';
import { DecoratedTooltip } from '@hotjar/insights-common';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { SITE } from '@hotjar/webapp/constants/routes';
import { usePageTitle } from '@hotjar/webapp/hooks/usePageTitle';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { CreateOrgModal } from './CreateOrgModal';
import { NewSiteCostConfirmation } from './NewSiteCostConfirmation';
import { DeclarationCheckbox } from './DeclarationCheckbox';
import { useNewSitePlan } from './hooks/useNewSitePlan';
import { AddNewButton, PageSection, StyledField } from './styles';
import type { CreateSiteForm } from './hooks/useCreateSite';
import { useCreateSite } from './hooks/useCreateSite';

export const CreateSitePage = (): JSX.Element => {
  usePageTitle('New Site - Hotjar');

  const [createOrgModalState, setCreateOrgModalState] = useState(false);
  const { t } = useTranslation('account');
  const { data: currentUser, isLoading: currentUserIsLoading } = useCurrentUser();
  const { data: ownedAccount, isLoading: ownedAccountIsLoading } = useOwnedAccount();
  const { isPermissionFetching } = useAuthorization();
  const { register, handleSubmit, errors, watch, setValue, control } = useForm<CreateSiteForm>({
    mode: 'onBlur',
  });
  const isBillingV3 = isBillingV3Enabled(ownedAccount);

  const selectedOrgId = watch('org');
  const isPageLoading = currentUserIsLoading || ownedAccountIsLoading || isPermissionFetching;

  const {
    productCatalog,
    activeMultipleSkuPlans,
    setActiveMultipleSkuPlan,
    isPaidPlan,
    trialInfo,
    candidateCampaign,
    sitePlanInfo,
    accountPricingTable,
    selectedPlan,
  } = useNewSitePlan();

  const {
    refetchPermissionsAndOrgs,
    adminOrganizations,
    selectedOrg,
    hasSubscriptionManagePermission,
    onCreate,
    isLoading,
  } = useCreateSite(selectedOrgId, sitePlanInfo, trialInfo);

  const hasCreatePermission = adminOrganizations.length > 0;

  const onOrgCreated = useCallback(
    (orgId: number) => {
      refetchPermissionsAndOrgs().then(() => {
        setValue('org', orgId);
        setCreateOrgModalState(false);
      });
    },
    [refetchPermissionsAndOrgs, setValue],
  );

  return (
    <Page>
      <CreateOrgModal
        isOpen={createOrgModalState}
        setModalState={(isOpen) => setCreateOrgModalState(isOpen)}
        onCreated={onOrgCreated}
      />
      <PageHeader
        title={t('create-site.title')}
        breadcrumbItems={
          <Breadcrumbs.Item href={getRoute(SITE.LIST.path)}>
            {t('create-site.sites-and-orgs')}
          </Breadcrumbs.Item>
        }
      />

      {!isPageLoading && !hasCreatePermission && (
        <PageSection>
          <Alert kind="danger">{t('create-site.permission-error')}</Alert>
        </PageSection>
      )}

      {hasCreatePermission && (
        <form onSubmit={handleSubmit(onCreate)}>
          <PageSection>
            <div>
              <div className="page-section">
                <div>
                  <StyledField
                    inputId="org"
                    labelText={t('create-site.organization')}
                    invalidText={errors?.org?.message}
                    isInvalid={!!errors.org}
                    isRequired
                  >
                    {currentUser?.isAccountAdmin && (
                      <AddNewButton onClick={() => setCreateOrgModalState(true)}>
                        {t('create-site.add-new')}
                      </AddNewButton>
                    )}
                    <Select
                      id="org"
                      name="org"
                      ref={register({ required: t('create-site.organization-required') as string })}
                      isInvalid={!!errors.org}
                    >
                      <option value="">{t('create-site.please-select')}</option>
                      {adminOrganizations?.map((item) => (
                        <option value={item.id} key={`Org_${item.id}`}>
                          {item.name}
                        </option>
                      ))}
                    </Select>
                  </StyledField>

                  <StyledField inputId="name" labelText={t('create-site.site-name')}>
                    <Input
                      id="name"
                      name="name"
                      placeholder={t('create-site.site-name-placeholder')}
                      inputRef={register({})}
                    />
                  </StyledField>

                  <StyledField
                    inputId="url"
                    labelText={t('create-site.site-url')}
                    invalidText={errors?.url?.message}
                    isInvalid={!!errors.url}
                    isRequired
                    helperText={
                      <Trans t={t} i18nKey="create-site.website-helper-text">
                        <DecoratedTooltip text="http://www.site.com" />
                        <DecoratedTooltip text="blog.site.com" />
                        <DecoratedTooltip text="site.com/blog" />
                      </Trans>
                    }
                  >
                    <Input
                      id="url"
                      name="url"
                      placeholder={t('create-site.site-url-placeholder')}
                      inputRef={register({
                        required: t('create-site.site-url-required') as string,
                        validate: (value: string) =>
                          validateHTTPSUrl(value) || (t('create-site.site-url-required') as string),
                      })}
                      isError={!!errors.url}
                    />
                  </StyledField>

                  <StyledField
                    inputId="industry"
                    labelText={t('create-site.industry')}
                    invalidText={errors?.industry?.message}
                    isInvalid={!!errors.industry}
                    isRequired
                  >
                    <Select
                      id="industry"
                      name="industry"
                      ref={register({ required: t('create-site.industry-required') as string })}
                      isInvalid={!!errors.industry}
                    >
                      <option value="">{t('create-site.please-select')}</option>
                      {SITE_INDUSTRY_CHOICES.map((item) => (
                        <option value={item.value} key={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Select>
                  </StyledField>

                  {selectedOrg && (
                    <div
                      css={css`
                        margin-bottom: ${tokens.spacer.x3};
                      `}
                    >
                      <div>
                        <Controller
                          control={control}
                          name="declaration"
                          rules={{ required: t<string>('create-site.declaration-checkbox.error') }}
                          render={(field) => (
                            <DeclarationCheckbox
                              ownerName={selectedOrg.name}
                              isAccountAdmin={currentUser?.isAccountAdmin ?? false}
                              isChecked={field.value}
                              onChange={field.onChange}
                              errorMessage={errors?.declaration?.message}
                              onCreateNewOrg={() => setCreateOrgModalState(true)}
                            />
                          )}
                        />
                      </div>

                      <div>
                        {!hasSubscriptionManagePermission && (
                          <Alert kind="info" title={t('create-site.subscription-plan')}>
                            {t('create-site.alerts.free-plan', { ownerName: selectedOrg?.name })}
                          </Alert>
                        )}

                        {hasSubscriptionManagePermission && ownedAccount?.lock && (
                          <Alert kind="info" title={t('create-site.subscription-plan')}>
                            {t('create-site.alerts.outstanding-invoice-1')}
                            <br />
                            {t('create-site.alerts.outstanding-invoice-2')}
                          </Alert>
                        )}

                        {hasSubscriptionManagePermission && trialInfo && !ownedAccount?.lock && (
                          <Alert kind="info" title={t('create-site.subscription-plan')}>
                            {t('create-site.alerts.trial-has-not-expired', {
                              plan: capitalize(candidateCampaign?.plan),
                            })}
                          </Alert>
                        )}

                        {hasSubscriptionManagePermission &&
                          !trialInfo &&
                          ownedAccount &&
                          !ownedAccount.hasPaymentDetails &&
                          !ownedAccount.lock && (
                            <Alert kind="info" title={t('create-site.subscription-plan')}>
                              {t('create-site.alerts.no-payment-method-1', {
                                context: isBillingV3 ? 'billingV3' : null,
                              })}
                              <br />
                              {t('create-site.alerts.no-payment-method-2', {
                                context: isBillingV3 ? 'billingV3' : null,
                              })}
                            </Alert>
                          )}

                        {hasSubscriptionManagePermission &&
                          ownedAccount?.hasPaymentDetails &&
                          !ownedAccount.lock && (
                            <div>
                              <Text as="label" textType="subtitle2">
                                {t('create-site.subscription-plan')}
                              </Text>
                              <div>
                                <MultiSkuComparisonLinks
                                  siteAccount={ownedAccount}
                                  onChange={(selectedProduct) => {
                                    setActiveMultipleSkuPlan(selectedProduct.type, selectedProduct);
                                  }}
                                  productCatalog={productCatalog}
                                  activeAnalyticsProductSku={
                                    activeMultipleSkuPlans[ProductType.Analytics]?.sku
                                  }
                                  activeFeedbackProductSku={
                                    activeMultipleSkuPlans[ProductType.Feedback]?.sku
                                  }
                                />
                              </div>
                            </div>
                          )}

                        {isPaidPlan && ownedAccount && !ownedAccount.lock && (
                          <div
                            css={css`
                              margin-top: ${tokens.spacer.x3};
                            `}
                          >
                            <Controller
                              control={control}
                              name="priceConfirmation"
                              rules={{
                                required: t<string>('create-site.price-confirmation-error'),
                              }}
                              render={(field) => (
                                <NewSiteCostConfirmation
                                  isChecked={field.value}
                                  onChange={field.onChange}
                                  errorMessage={errors?.priceConfirmation?.message}
                                  accountPricingTable={accountPricingTable}
                                  selectedPlanId={selectedPlan?.identifier ?? '-1'}
                                  selectedOrgId={selectedOrgId}
                                />
                              )}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <Button isLoading={isLoading || isPageLoading} type="submit">
                    {t('create-site.submit-button')}
                  </Button>
                </div>
              </div>
            </div>
          </PageSection>
        </form>
      )}
    </Page>
  );
};
